/**
 * AccessEditorDrawer — edit the per-app grants for one user.
 *
 * The drawer shows every app from the registry and the user's current
 * grant for it (if any). Compliance officer toggles a checkbox to grant
 * or revoke. Permanent vs temporary is set per row.
 *
 * Submits diffs to PATCH /api/admin/team/:userId/access with two arrays:
 *   - grants:  new grants to insert
 *   - revoke:  grant ids OR app keys to revoke
 *
 * A "Offboard" button revokes everything in one click (with confirmation).
 */
import React, { useEffect, useMemo, useState } from 'react';
import HipaaAckBanner from './HipaaAckBanner';

const API = process.env.REACT_APP_API_URL || '';

function thirtyDaysFromNow() {
  const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10);
}

/**
 * DepartmentMemberships — "Also oversees" section. The user's primary
 * department never changes here; these are additional departments whose
 * items, policies, and reviews the user can see. Each grant/removal is
 * written immediately and audited with the grantor.
 */
function DepartmentMemberships({ user }) {
  const [memberships, setMemberships] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    try {
      const [m, d] = await Promise.all([
        fetch(`${API}/api/user-departments/${user.id}`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/departments`, { credentials: 'include' }).then(r => r.json()),
      ]);
      if (m.success) setMemberships(m.memberships || []);
      setDepartments(Array.isArray(d) ? d : (d.departments || []));
    } catch { /* section stays empty */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user.id]);

  const grant = async () => {
    if (!pick || busy) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/user-departments`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, department_id: pick }),
      }).then(x => x.json());
      if (!r.success) setErr(r.message || 'Could not add the department.');
      else { setPick(''); await load(); }
    } catch { setErr('Could not add the department.'); }
    setBusy(false);
  };

  const revoke = async (id) => {
    if (busy) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/user-departments/${id}`, { method: 'DELETE', credentials: 'include' }).then(x => x.json());
      if (!r.success) setErr(r.message || 'Could not remove the department.');
      else await load();
    } catch { setErr('Could not remove the department.'); }
    setBusy(false);
  };

  const memberIds = new Set(memberships.map(m => m.department_id));
  const options = departments.filter(d => !memberIds.has(d.id) && d.id !== user.department_id);

  return (
    <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
      <div style={{ fontSize: 13.5, color: '#0f172a', fontWeight: 600 }}>Also oversees</div>
      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, marginBottom: 8 }}>
        Additional departments this person sees across the platform — their items, policies, and reviews. Their primary department and role do not change, and this never grants compliance-level access.
      </div>
      {memberships.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {memberships.map(m => (
            <span key={m.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 999, padding: '4px 10px', color: '#334155' }}>
              {m.department_name}
              <button type="button" onClick={() => revoke(m.id)} disabled={busy} title={`Remove ${m.department_name}`}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 12, padding: 0 }}>
                remove
              </button>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <select value={pick} onChange={e => setPick(e.target.value)}
          style={{ flex: 1, padding: '6px 8px', fontSize: 12.5, border: '1px solid #cbd5e1', borderRadius: 6, background: '#ffffff' }}>
          <option value="">Add a department…</option>
          {options.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <button type="button" onClick={grant} disabled={!pick || busy}
          style={{ background: '#1e293b', color: '#ffffff', border: '1px solid #1e293b', padding: '6px 14px', fontSize: 12.5, cursor: busy ? 'wait' : 'pointer', borderRadius: 6, opacity: !pick ? 0.5 : 1 }}>
          Add
        </button>
      </div>
      {err && <div role="alert" style={{ marginTop: 6, fontSize: 12, color: '#b91c1c' }}>{err}</div>}
    </div>
  );
}

export default function AccessEditorDrawer({ user, apps, onClose, onSaved }) {
  // Pull the user's current effective grants by app
  const currentByApp = useMemo(() => {
    const m = new Map();
    for (const g of (user.access || [])) {
      if (g.active) m.set(g.app_key, g);
    }
    return m;
  }, [user]);

  // draft state — each row: { enabled, access_role, durationMode, expires_at }
  const [draft, setDraft] = useState(() => {
    const out = {};
    for (const a of (apps || [])) {
      const cur = currentByApp.get(a.key);
      out[a.key] = {
        enabled:      !!cur,
        access_role:  cur?.access_role || 'participant',
        durationMode: cur?.expires_at ? 'temporary' : 'permanent',
        expires_at:   cur?.expires_at ? new Date(cur.expires_at).toISOString().slice(0, 10) : thirtyDaysFromNow(),
        grant_id:     cur?.id || null,
      };
    }
    return out;
  });

  const [reason, setReason] = useState('');
  const [hipaaAck, setHipaaAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const updateRow = (key, patch) => setDraft(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const hasNewGrant = useMemo(() => {
    for (const a of (apps || [])) {
      const d = draft[a.key]; const cur = currentByApp.get(a.key);
      if (!cur && d.enabled) return true;
      if (cur && d.enabled) {
        // Change to expiry or access_role counts as a new grant
        const curExp = cur.expires_at ? new Date(cur.expires_at).toISOString().slice(0, 10) : null;
        const draftExp = d.durationMode === 'permanent' ? null : d.expires_at;
        if (cur.access_role !== d.access_role) return true;
        if (curExp !== draftExp) return true;
      }
    }
    return false;
  }, [draft, apps, currentByApp]);

  const submit = async () => {
    setError(null);
    if (hasNewGrant && !hipaaAck) { setError('Please acknowledge the HIPAA notice before saving.'); return; }
    setBusy(true);
    try {
      const grants = [];
      const revoke = [];
      for (const a of (apps || [])) {
        const d = draft[a.key]; const cur = currentByApp.get(a.key);
        const draftExp = d.durationMode === 'permanent' ? null : (d.expires_at + 'T23:59:59Z');
        const curExp = cur?.expires_at || null;
        if (!cur && d.enabled) {
          grants.push({ app_key: a.key, access_role: d.access_role, expires_at: draftExp });
        } else if (cur && !d.enabled) {
          revoke.push(cur.id);
        } else if (cur && d.enabled && (cur.access_role !== d.access_role || (curExp && new Date(curExp).toISOString().slice(0,10)) !== (draftExp && draftExp.slice(0,10)))) {
          // Re-grant: revoke old + insert new
          revoke.push(cur.id);
          grants.push({ app_key: a.key, access_role: d.access_role, expires_at: draftExp });
        }
      }

      const r = await fetch(`${API}/api/admin/team/${user.id}/access`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grants, revoke, reason: reason || null, hipaa_ack: hipaaAck }),
      });
      const j = await r.json();
      if (!r.ok || !j.success) setError(j.message || 'Could not save.');
      else if (typeof onSaved === 'function') onSaved();
    } catch (e) { setError(e.message || 'Could not save.'); }
    finally { setBusy(false); }
  };

  const offboard = async () => {
    if (!window.confirm(`Offboard ${user.email}? This revokes every active grant. The user is not deleted.`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/team/${user.id}/offboard`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'manual_offboard' }),
      });
      const j = await r.json();
      if (!r.ok || !j.success) setError(j.message || 'Could not offboard.');
      else if (typeof onSaved === 'function') onSaved();
    } catch (e) { setError(e.message || 'Could not offboard.'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.20)', zIndex: 55 }} />
      <div role="dialog" aria-label="Edit access" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(640px, 94vw)',
        background: '#ffffff', borderLeft: '1px solid #cbd5e1', zIndex: 60,
        overflowY: 'auto',
      }}>
        <div style={{ position: 'sticky', top: 0, background: '#ffffff', padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', zIndex: 1 }}>
          <div>
            <div style={{ fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>Access for</div>
            <div style={{ fontSize: 16, color: '#0f172a', fontWeight: 600, marginTop: 4 }}>{user.name || user.email}</div>
            <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'ui-monospace, monospace', marginTop: 2 }}>{user.email}</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '4px 10px', fontSize: 12, color: '#475569', cursor: 'pointer', borderRadius: 6 }}>Close</button>
        </div>

        <div style={{ padding: '16px 20px 24px' }}>
          {(apps || []).map(a => {
            const d = draft[a.key] || {};
            return (
              <div key={a.key} style={{ borderBottom: '1px solid #f1f5f9', padding: '12px 0' }}>
                <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input type="checkbox" checked={d.enabled} onChange={(e) => updateRow(a.key, { enabled: e.target.checked })} style={{ marginTop: 3 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <div style={{ fontSize: 13.5, color: '#0f172a', fontWeight: 500 }}>{a.label}</div>
                      {d.enabled && (
                        <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>
                          {d.durationMode === 'permanent' ? 'permanent' : `until ${d.expires_at}`}
                        </span>
                      )}
                    </div>
                    {a.description && (
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{a.description}</div>
                    )}
                    {d.enabled && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8, fontSize: 12 }}>
                        <select value={d.access_role} onChange={(e) => updateRow(a.key, { access_role: e.target.value })}
                          style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 6, background: '#ffffff' }}>
                          <option value="participant">Participant</option>
                          <option value="viewer">Viewer (read-only)</option>
                        </select>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <input type="radio" checked={d.durationMode === 'permanent'} onChange={() => updateRow(a.key, { durationMode: 'permanent' })} />
                          Permanent
                        </label>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <input type="radio" checked={d.durationMode === 'temporary'} onChange={() => updateRow(a.key, { durationMode: 'temporary' })} />
                          Until
                        </label>
                        {d.durationMode === 'temporary' && (
                          <input type="date" value={d.expires_at} onChange={(e) => updateRow(a.key, { expires_at: e.target.value })}
                            style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 6 }} />
                        )}
                      </div>
                    )}
                  </div>
                </label>
              </div>
            );
          })}

          <DepartmentMemberships user={user} />

          <label style={{ fontSize: 11, color: '#475569', display: 'block', marginTop: 16, marginBottom: 3 }}>Reason (audit note)</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={240}
            placeholder="Why these changes (optional)"
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6, resize: 'vertical' }} />

          {hasNewGrant && (
            <HipaaAckBanner checked={hipaaAck} onChange={setHipaaAck} />
          )}

          {error && (
            <div role="alert" style={{ marginTop: 10, fontSize: 12, color: '#b91c1c' }}>{error}</div>
          )}

          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button type="button" onClick={offboard} disabled={busy}
              style={{ background: 'transparent', border: '1px solid #fecaca', color: '#b91c1c', padding: '8px 14px', fontSize: 13, cursor: 'pointer', borderRadius: 6 }}>
              Offboard (revoke all)
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={onClose} disabled={busy}
                style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '8px 14px', fontSize: 13, color: '#475569', cursor: 'pointer', borderRadius: 6 }}>Cancel</button>
              <button type="button" onClick={submit} disabled={busy || (hasNewGrant && !hipaaAck)}
                style={{ background: '#1e293b', color: '#ffffff', border: '1px solid #1e293b', padding: '8px 14px', fontSize: 13, cursor: busy ? 'wait' : 'pointer', borderRadius: 6 }}>
                {busy ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
